import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import {
  AutomationMessageSourceIcon, AutomationMessageTypeIcon
} from '@app/components/AutomationMessageIcons';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { JsonViewer } from '@app/components/JsonViewer';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { RemoveButton } from '@app/components/RemoveButton';
import { useEntity } from '@app/hooks/useEntity';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import {
  AutomationMessage, SOURCE_ABOUT, SOURCE_ORDER, TYPE_RANK, compareSource, sourceDetail,
  sourceLabel, sourceOf
} from '@app/model/AutomationMessage';
import { AutomationMessageType } from '@app/model/Enums';
import { useAutomationMessageStore } from '@app/store/entityStore';
import {
  HHmmFormat, dateFormater, ddMMyyyyFormat, ddMMyyyyParse, fromIsoDate, toIsoDate
} from '@app/utils/functions';
import {
  ActionList,
  ActionListItem,
  AlertVariant,
  Badge,
  Bullseye,
  Button,
  Card,
  CodeBlock,
  CodeBlockCode,
  DatePicker,
  Divider,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Pagination,
  PaginationVariant,
  SearchInput,
  Select,
  SelectOption,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarFilter,
  ToolbarGroup,
  ToolbarItem,
  ToolbarLabel,
  ToolbarLabelGroup,
  ToolbarToggleGroup,
  Tooltip
} from '@patternfly/react-core';
import {
  EnvelopeIcon,
  EnvelopeOpenIcon,
  FilterIcon,
  SearchIcon
} from '@patternfly/react-icons';
import { ExpandableRowContent, InnerScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

/** One entry per filterable field holding text or enumerated values, and the values chosen for it. */
type FilterSet = { type: string[]; source: string[]; entity: string[]; key: string[] };

const emptyFilters = (): FilterSet => ({ type: [], source: [], entity: [], key: [] });

/** Filter category, as the dropdown names it, to the field it filters. */
const CATEGORY_MAP: Record<string, keyof FilterSet> = {
  Type: 'type',
  Source: 'source',
  Entity: 'entity',
  Key: 'key'
};

type DateRange = { start: string; end: string };

/**
 * The two dates, each filterable on its own range: "raised in the last week" and "still being
 * reported this week" are different questions, and a single range could only answer one of them.
 * The value is the field on the message, so the predicate and the chip both read off this.
 */
const DATE_CATEGORIES = { Created: 'dateCreated', Updated: 'dateUpdated' } as const;
type DateCategory = keyof typeof DATE_CATEGORIES;

const emptyRange = (): DateRange => ({ start: '', end: '' });

/** Secondary text - the time under a date, and the dash standing in for one that is missing. */
const subtleText = 'var(--pf-t--global--text--color--subtle, #6a6e73)';

/** The band a class's section is headed by, a shade off the rows so it reads as a header. */
const groupHeaderBackground = 'var(--pf-t--global--background--color--secondary--default, #f2f2f2)';

/**
 * Column order, named so the sort cases and the header cells cannot drift apart.
 *
 * The dates come before Message rather than after it, which reads as an odd place for them until
 * you widen the window: Message is the one column that has to take whatever width is left, and the
 * Actions column is frozen to the right edge. Anything sitting between them is what the freeze
 * covers up, and a date column permanently hidden behind Actions is worse than a long message
 * losing its tail to a horizontal scroll it can be scrolled back into.
 */
const COLUMN = { type: 0, source: 1, entity: 2, key: 3, created: 4, updated: 5, message: 6 } as const;

/** Every cell in a row, plus the expand toggle's own column. */
const COLUMN_COUNT = 9;

/** "A and B", or "A, B and C" - the section header reads as a sentence, not as a list of values. */
const joinLabels = (labels: string[]): string => (
  labels.length < 2
    ? labels[0] ?? ''
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1] ?? ''}`
);

/**
 * What the table is sectioned on: the class a message is about, which is the entity and the key
 * together and neither of them alone. The entity names what kind of thing it is and the key which
 * one, and a key is only unique within an entity - the ad hoc sheet's ADR1234 and an offering id
 * are both keys.
 *
 * A NUL joins them so no pair of values can be made to collide by the separator: keys carry dashes
 * and colons, and 'Klass' + '-1' must not read as the same section as 'Klass-' + '1'.
 */
const classOf = (msg: AutomationMessage): string => `${msg.entity ?? ''}\u0000${msg.key ?? ''}`;

/**
 * Sections in entity then key order, which is what settles the order of two sections the sorted
 * column cannot separate. Keys are the reporting system's ids and numeric for most entities, so
 * compared numerically - otherwise 63108503 sorts before 9.
 */
const compareClass = (a: AutomationMessage, b: AutomationMessage): number =>
  (a.entity ?? '').localeCompare(b.entity ?? '')
  || (a.key ?? '').localeCompare(b.key ?? '', undefined, { numeric: true });

/**
 * Whether a message's date falls inside a range, both ends optional.
 *
 * Compared as "yyyy-MM-dd" in the browser's zone, which is the zone the picker's dates were read
 * in - so a range closing on the 24th includes everything reported on the 24th locally, however
 * the instant is written on the wire.
 *
 * A message with no date matches no range, rather than matching every one of them: the rows that
 * predate the timestamp columns must not turn up under a filter that claims to name when they
 * arrived. They are what the unfiltered list is for.
 */
const withinRange = (value: string | null | undefined, range: DateRange | null): boolean => {
  if (!range || (!range.start && !range.end)) return true;
  if (!value) return false;

  const day = toIsoDate(new Date(value));
  if (range.start && day < range.start) return false;
  if (range.end && day > range.end) return false;
  return true;
};

const AutomationMessages: React.FunctionComponent = () => {
  const { addNotification } = useNotification();

  const { items: messages, error, retry } = useEntity(useAutomationMessageStore);

  const upsertMessage = useAutomationMessageStore(state => state.upsertItem);
  const saveMessage = useAutomationMessageStore(state => state.save);
  const deleteMessage = useAutomationMessageStore(state => state.delete);

  const [page, setPage] = React.useState<number>(1);
  const { perPage, setPerPage } = useTablePreferences('automation-messages', 10);

  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = React.useState(false);

  // --- Filter State ---
  const [filters, setFilters] = React.useState<FilterSet>(emptyFilters);
  const [appliedRanges, setAppliedRanges] = React.useState<Record<DateCategory, DateRange | null>>({
    Created: null,
    Updated: null
  });
  // What the pickers are showing, which is not yet what is being filtered on - a range only takes
  // effect on Apply, so a half-entered one does not empty the table between the two dates.
  const [rangeInputs, setRangeInputs] = React.useState<Record<DateCategory, DateRange>>({
    Created: emptyRange(),
    Updated: emptyRange()
  });

  const [currentCategory, setCurrentCategory] = React.useState<string>('Type');
  const [isCategoryOpen, setIsCategoryOpen] = React.useState(false);
  const [isTypeOpen, setIsTypeOpen] = React.useState(false);
  const [isSourceOpen, setIsSourceOpen] = React.useState(false);
  const [isEntityOpen, setIsEntityOpen] = React.useState(false);
  const [entitySearch, setEntitySearch] = React.useState('');
  const [keyInput, setKeyInput] = React.useState('');

  // --- Sort State ---
  // Newest revision first, because this is a log: the message whose report last changed is the one
  // worth reading, and 1,700-odd rows in the service's own insertion order buries it. Both nulls
  // sort as 0, so the messages that predate the timestamps sit at the far end rather than on top.
  const [activeSortIndex, setActiveSortIndex] = React.useState<number | null>(COLUMN.updated);
  const [activeSortDirection, setActiveSortDirection] = React.useState<'asc' | 'desc' | null>('desc');

  // --- Derived State ---

  /**
   * The entities to offer, gathered from the messages themselves rather than from a list of names
   * kept here: what the automation reports on is its business, and a fixed list would go stale
   * silently - offering an entity nothing is filed under, or missing one that is.
   */
  const availableEntities = React.useMemo(() => {
    const names = new Set<string>();
    messages.forEach(msg => { if (msg.entity) names.add(msg.entity); });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [messages]);

  /**
   * Both sources are offered whether or not anything is filed under them - unlike the entities,
   * which are whatever the automation happens to report on: these two are the systems that write
   * the panel, and one of them having nothing to say today is worth seeing rather than hiding. Any
   * source the messages carry that this build cannot name is added, so a message from a source
   * introduced after it can still be filtered on and not only scrolled past.
   */
  const availableSources = React.useMemo(() => {
    const names = new Set<string>(SOURCE_ORDER);
    messages.forEach(msg => names.add(sourceOf(msg)));
    return Array.from(names).sort(compareSource);
  }, [messages]);

  const filteredRows = React.useMemo(() => messages.filter(msg => {
    const matchesType = filters.type.length === 0 || filters.type.includes(msg.type as string);
    const matchesSource = filters.source.length === 0 || filters.source.includes(sourceOf(msg));
    const matchesEntity = filters.entity.length === 0 || filters.entity.includes(msg.entity ?? '');
    // Text, so a substring counts and any one of the terms entered is enough.
    const matchesKey = filters.key.length === 0
      || filters.key.some(term => msg.key?.toLowerCase().includes(term.toLowerCase()));

    return matchesType && matchesSource && matchesEntity && matchesKey
      && withinRange(msg.dateCreated, appliedRanges.Created)
      && withinRange(msg.dateUpdated, appliedRanges.Updated);
  }), [messages, filters, appliedRanges]);

  /**
   * Sectioned by the class a message is about, and ordered within the section.
   *
   * The section is the class because that is the question a reader of this panel has: what is wrong
   * with this one class. Both systems report the same class and the service now files their findings
   * under the one entity name, so those findings sit together here - the sheet's and this service's
   * on one class, one after the other, with the Source column saying which is which. Sectioning on
   * the source instead would put them back in two lists.
   *
   * Ordered here, over the whole filtered list, rather than by grouping each page's rows as they are
   * drawn: a section has to be one section, and grouping only what a page happens to hold would
   * scatter a class's messages across every page of the table under a header repeated on each.
   *
   * The sorted column orders the rows inside a section and the sections by their leading row, so a
   * click on Updated still brings what last changed to the top of the table - it just brings the
   * class it belongs to with it rather than the one row. Sections the column cannot separate fall
   * back to entity and key, and with no column sorted at all the rows keep the order the service
   * sent them in, Array.sort being stable.
   */
  const sortedRows = React.useMemo(() => {
    const instant = (value: string | null | undefined) => (value ? new Date(value).getTime() : 0);

    const compare = (a: AutomationMessage, b: AutomationMessage): number => {
      switch (activeSortIndex) {
        case COLUMN.type:
          return (TYPE_RANK[a.type] ?? -1) - (TYPE_RANK[b.type] ?? -1);
        // The laid-out order of the sources rather than the alphabet, so ascending reads sheet
        // import first however the two are spelt - see SOURCE_ORDER.
        case COLUMN.source:
          return compareSource(sourceOf(a), sourceOf(b));
        case COLUMN.entity:
          return (a.entity ?? '').localeCompare(b.entity ?? '');
        // Keys are the reporting system's ids, which are numeric for most entities - so compared
        // numerically, or 63108503 and 9 would come back in that order.
        case COLUMN.key:
          return (a.key ?? '').localeCompare(b.key ?? '', undefined, { numeric: true });
        case COLUMN.created:
          return instant(a.dateCreated) - instant(b.dateCreated);
        case COLUMN.updated:
          return instant(a.dateUpdated) - instant(b.dateUpdated);
        default:
          return 0;
      }
    };

    const isSorted = activeSortIndex !== null && activeSortDirection !== null;
    const directed = (a: AutomationMessage, b: AutomationMessage) =>
      (activeSortDirection === 'desc' ? -compare(a, b) : compare(a, b));

    // Kept as a map from the class to its messages: a section is every message about one class
    // wherever the service happened to list them, which is what a pass over a sorted array cannot
    // do - two reports about one class need not have arrived anywhere near each other.
    const sections = new Map<string, AutomationMessage[]>();
    filteredRows.forEach(row => {
      const id = classOf(row);
      const held = sections.get(id);
      if (held) held.push(row);
      else sections.set(id, [row]);
    });

    const ordered = Array.from(sections.values());
    if (isSorted) ordered.forEach(rows => rows.sort(directed));
    // By the leading row, which after the sort above is the section's best under the same
    // comparison - so the section a reader is sorting towards comes first, whole. Every section is
    // created around a row, so the two leads are always there; the guard is what says so to the
    // compiler, an index being unchecked.
    ordered.sort((sectionA, sectionB) => {
      const [leadA] = sectionA;
      const [leadB] = sectionB;
      if (!leadA || !leadB) return 0;
      return (isSorted ? directed(leadA, leadB) : 0) || compareClass(leadA, leadB);
    });

    return ordered.flat();
  }, [filteredRows, activeSortIndex, activeSortDirection]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return sortedRows.slice(startIdx, startIdx + perPage);
  }, [sortedRows, page, perPage]);

  /**
   * The page's rows cut into the runs of one class they already sit in, each carrying where it
   * starts in the page so a row keeps the page-wide index the table's range selection counts on.
   *
   * A run and not a bucket: the list arrives sectioned, so this only has to find where one section
   * ends. A section wider than a page therefore comes out as one run here and another on the next
   * page, each under its own header, which is what a reader paging through a long one wants to see
   * at the top of the page they land on.
   */
  const pageSections = React.useMemo(() => {
    // What a header says about its class, counted over the filtered list and not over the page: a
    // header reading two where the class has nine findings would be read as all of them, and how
    // much there is to work through on one class is the number worth having.
    const summaries = new Map<string, { count: number; sources: string[] }>();
    filteredRows.forEach(row => {
      const id = classOf(row);
      const held = summaries.get(id) ?? { count: 0, sources: [] };
      const source = sourceOf(row);
      held.count += 1;
      if (!held.sources.includes(source)) held.sources.push(source);
      summaries.set(id, held);
    });

    type Section = {
      id: string; entity: string; key: string;
      firstIndex: number; total: number; sources: string[]; rows: AutomationMessage[];
    };
    const sections: Section[] = [];

    paginatedRows.forEach((row, index) => {
      const id = classOf(row);
      const open = sections[sections.length - 1];

      if (open && open.id === id) {
        open.rows.push(row);
        return;
      }

      const summary = summaries.get(id);
      sections.push({
        id,
        entity: row.entity,
        key: row.key,
        firstIndex: index,
        total: summary?.count ?? 1,
        sources: [...(summary?.sources ?? [])].sort(compareSource),
        rows: [row]
      });
    });

    return sections;
  }, [paginatedRows, filteredRows]);

  const activeFilterCount = React.useMemo(() => (
    Object.values(filters).reduce((total, values) => total + values.length, 0)
    + Object.values(appliedRanges).filter(Boolean).length
  ), [filters, appliedRanges]);

  // A page reached before the filters narrowed the list can point past the end of it. Fall back to
  // the last page holding rows rather than showing an empty state over a list that plainly has
  // some; a filter matching nothing at all keeps its own empty state, there being no last page.
  React.useEffect(() => {
    const lastPage = Math.ceil(filteredRows.length / perPage);
    if (lastPage > 0 && page > lastPage) setPage(lastPage);
  }, [filteredRows.length, perPage, page]);

  // A mark on a message the filters have since hidden is a mark the user cannot see, and Delete
  // Selected would act on it unannounced. So filtering drops whatever it hides out of the
  // selection; paging does not, a selection being meant to build up across pages.
  React.useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const stillShown = new Set(filteredRows.map(row => row.id));
      const kept = [...prev].filter(id => stillShown.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [filteredRows]);

  // --- Filter Handlers ---

  /** Adds or removes one value from a multi-select field. */
  const toggleFilterValue = (field: keyof FilterSet, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: prev[field].includes(value) ? prev[field].filter(v => v !== value) : [...prev[field], value]
    }));
    setPage(1);
  };

  const onKeySearch = (value: string) => {
    const term = value.trim();
    if (!term || filters.key.includes(term)) return;
    setFilters(prev => ({ ...prev, key: [...prev.key, term] }));
    setKeyInput('');
    setPage(1);
  };

  const applyRange = (category: DateCategory) => {
    setAppliedRanges(prev => ({ ...prev, [category]: { ...rangeInputs[category] } }));
    setPage(1);
  };

  const setRangeEnd = (category: DateCategory, end: 'start' | 'end', date: Date | undefined) => {
    setRangeInputs(prev => ({
      ...prev,
      [category]: { ...prev[category], [end]: date && !isNaN(date.getTime()) ? toIsoDate(date) : '' }
    }));
  };

  const clearRange = (category: DateCategory) => {
    setAppliedRanges(prev => ({ ...prev, [category]: null }));
    setRangeInputs(prev => ({ ...prev, [category]: emptyRange() }));
    setPage(1);
  };

  const onDeleteFilter = (category: string | ToolbarLabelGroup, label: string | ToolbarLabel) => {
    const categoryName = typeof category === 'string' ? category : category.name;

    if (categoryName in DATE_CATEGORIES) {
      clearRange(categoryName as DateCategory);
      return;
    }

    const field = CATEGORY_MAP[categoryName];
    if (!field) return;

    // A chip carrying a node - Type's, which shows its icon - identifies itself by key.
    const value = typeof label === 'string' ? label : String(label.key);
    setFilters(prev => ({ ...prev, [field]: prev[field].filter(v => v !== value) }));
    setPage(1);
  };

  const clearAllFilters = () => {
    setFilters(emptyFilters());
    setAppliedRanges({ Created: null, Updated: null });
    setRangeInputs({ Created: emptyRange(), Updated: emptyRange() });
    setEntitySearch('');
    setKeyInput('');
    setPage(1);
  };

  // --- Row Handlers ---
  const handleToggleRow = async (row: AutomationMessage) => {
    const newExpanded = new Set(expandedIds);
    const isExpanding = !newExpanded.has(row.id);

    if (isExpanding) {
      newExpanded.add(row.id);
      if (!row.read) await handleMarkReadStatus([row.id], true, false);
    } else {
      newExpanded.delete(row.id);
    }
    setExpandedIds(newExpanded);
  };

  const handleSelectRow = (id: number, isSelected: boolean) => {
    const newSelected = new Set(selectedIds);
    if (isSelected) newSelected.add(id);
    else newSelected.delete(id);
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedIds(new Set(paginatedRows.map(row => row.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

const handleMarkReadStatus = async (idsToUpdate: number[], isRead: boolean, showNotification = true) => {
    if (idsToUpdate.length === 0) return;

    // Instantly updates the cache so the UI feels perfectly responsive
    idsToUpdate.forEach(id => {
      const msg = messages.find(m => m.id === id);
      if (msg) upsertMessage({ ...msg, read: isRead });
    });

    // 2. Persist to backend
    try {
      await Promise.all(idsToUpdate.map(id => {
        const msg = messages.find(m => m.id === id);
        // The 'false' tells the store this is an UPDATE (PUT), not a CREATE (POST)
        if (msg) return saveMessage({ ...msg, read: isRead }, false);
        return Promise.resolve();
      }));

      if (showNotification && idsToUpdate.length > 1) {
        addNotification(`Marked ${idsToUpdate.length} messages as ${isRead ? 'read' : 'unread'}`, AlertVariant.success);
      }
    } catch (error: any) {
      addNotification(error.error || 'Failed to update message status', AlertVariant.danger);
    }
  };

  const handleDelete = async (idsToDelete: number[]) => {
    if (idsToDelete.length === 0) return;
    beginLoading();
    try {
      await Promise.all(idsToDelete.map(id => deleteMessage(id)));
      setSelectedIds(new Set());
      addNotification(`Successfully deleted ${idsToDelete.length} message(s)`, AlertVariant.success);
    } catch (error: any) {
      addNotification(error.error || 'Failed to delete messages', AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  // --- UI Helpers ---
  const parseJSON = (val: string) => {
    if (!val) return 'No data';
    try {
      return typeof val === 'string' ? JSON.parse(val) : val;
    } catch {
      return val; // Fallback to raw string if the payload is somehow invalid
    }
  };

  /**
   * A stamp as a reader should see it, in their own zone, with the time under the date rather than
   * beside it: on one line these two columns take a third of the table between them, and the
   * Message column is what pays for it.
   *
   * The dash is not an error state: see AutomationMessage.dateCreated for why a message can have
   * no dates, and why nothing can fill them in retrospectively.
   */
  const renderDate = (value: string | null | undefined) => {
    if (!value) return <span style={{ color: subtleText }}>&mdash;</span>;
    return (
      <React.Fragment>
        <div>{ddMMyyyyFormat(value)}</div>
        <div style={{ color: subtleText }}>{HHmmFormat(value)}</div>
      </React.Fragment>
    );
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage);
      }}
      perPageOptions={[{ title: '10', value: 10 }, { title: '20', value: 20 }, { title: '50', value: 50 }, { title: '100', value: 100 }]}
      variant={variant as PaginationVariant}
    />
  );

  const getSortParams = (columnIndex: number) => ({
    sortBy: {
      index: activeSortIndex ?? undefined,
      direction: activeSortDirection ?? undefined,
      defaultDirection: 'asc' as const
    },
    onSort: (_event: React.MouseEvent, index: number, direction: 'asc' | 'desc') => {
      setActiveSortIndex(index);
      setActiveSortDirection(direction);
    },
    columnIndex
  });

  // --- Filter Toolbar ---

  const buildCategoryDropdown = () => (
    <ToolbarItem>
      <Select
        aria-label="Filter category"
        isOpen={isCategoryOpen}
        selected={currentCategory}
        onOpenChange={(isOpen) => setIsCategoryOpen(isOpen)}
        onSelect={(_event, value) => { setCurrentCategory(String(value)); setIsCategoryOpen(false); }}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsCategoryOpen(!isCategoryOpen)} isExpanded={isCategoryOpen}
            icon={<FilterIcon />} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            {currentCategory}
          </MenuToggle>
        )}
      >
        <SelectOption value="Type">Type</SelectOption>
        <SelectOption value="Source">Source</SelectOption>
        <SelectOption value="Entity">Entity</SelectOption>
        <SelectOption value="Key">Key</SelectOption>
        <SelectOption value="Created">Created</SelectOption>
        <SelectOption value="Updated">Updated</SelectOption>
      </Select>
    </ToolbarItem>
  );

  const buildTypeFilter = () => (
    <ToolbarFilter
      categoryName="Type"
      showToolbarItem={currentCategory === 'Type'}
      deleteLabel={onDeleteFilter}
      // Each chip carries its own severity icon, so a glance at the toolbar says what is being
      // looked at without reading the words.
      labels={filters.type.map(type => ({
        key: type,
        node: (
          <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsXs' }}>
            <FlexItem><AutomationMessageTypeIcon type={type as AutomationMessageType} /></FlexItem>
            <FlexItem>{type}</FlexItem>
          </Flex>
        )
      }))}
    >
      <Select
        aria-label="Type"
        isOpen={isTypeOpen}
        selected={filters.type}
        onOpenChange={(isOpen) => setIsTypeOpen(isOpen)}
        onSelect={(_event, value) => toggleFilterValue('type', String(value))}
        popperProps={{ minWidth: '150px' }}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsTypeOpen(!isTypeOpen)} isExpanded={isTypeOpen}
            style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            Filter by type {filters.type.length > 0 && <Badge isRead>{filters.type.length}</Badge>}
          </MenuToggle>
        )}
      >
        {Object.values(AutomationMessageType).map(type => (
          <SelectOption hasCheckbox key={type} value={type} isSelected={filters.type.includes(type)}>
            <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
              <FlexItem><AutomationMessageTypeIcon type={type} /></FlexItem>
              <FlexItem>{type}</FlexItem>
            </Flex>
          </SelectOption>
        ))}
      </Select>
    </ToolbarFilter>
  );

  const buildSourceFilter = () => (
    <ToolbarFilter
      categoryName="Source"
      showToolbarItem={currentCategory === 'Source'}
      deleteLabel={onDeleteFilter}
      // Keyed by the stored value and shown by its label: the chip has to read as the section it
      // narrows the table to, and onDeleteFilter takes the value back off the key.
      labels={filters.source.map(source => ({
        key: source,
        node: (
          <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsXs' }}>
            <FlexItem><AutomationMessageSourceIcon source={source} /></FlexItem>
            <FlexItem>{sourceLabel(source)}</FlexItem>
          </Flex>
        )
      }))}
    >
      <Select
        aria-label="Source"
        isOpen={isSourceOpen}
        selected={filters.source}
        onOpenChange={(isOpen) => setIsSourceOpen(isOpen)}
        onSelect={(_event, value) => toggleFilterValue('source', String(value))}
        popperProps={{ minWidth: '260px' }}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsSourceOpen(!isSourceOpen)} isExpanded={isSourceOpen}
            style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            Filter by source {filters.source.length > 0 && <Badge isRead>{filters.source.length}</Badge>}
          </MenuToggle>
        )}
      >
        {availableSources.map(source => (
          <SelectOption
            hasCheckbox
            key={source}
            value={source}
            isSelected={filters.source.includes(source)}
            description={SOURCE_ABOUT[source]?.blurb}
          >
            <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
              <FlexItem><AutomationMessageSourceIcon source={source} /></FlexItem>
              <FlexItem>{sourceLabel(source)}</FlexItem>
            </Flex>
          </SelectOption>
        ))}
      </Select>
    </ToolbarFilter>
  );

  const buildEntityFilter = () => {
    const shown = availableEntities.filter(name => name.toLowerCase().includes(entitySearch.toLowerCase()));

    return (
      <ToolbarFilter
        categoryName="Entity"
        showToolbarItem={currentCategory === 'Entity'}
        deleteLabel={onDeleteFilter}
        labels={filters.entity}
      >
        <Select
          aria-label="Entity"
          isOpen={isEntityOpen}
          selected={filters.entity}
          onOpenChange={(isOpen) => setIsEntityOpen(isOpen)}
          onSelect={(_event, value) => toggleFilterValue('entity', String(value))}
          popperProps={{ minWidth: '220px' }}
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle ref={toggleRef} onClick={() => setIsEntityOpen(!isEntityOpen)} isExpanded={isEntityOpen}
              style={{ width: '100%', verticalAlign: 'text-bottom' }}>
              Filter by entity {filters.entity.length > 0 && <Badge isRead>{filters.entity.length}</Badge>}
            </MenuToggle>
          )}
        >
          <div style={{ padding: '8px' }}>
            <SearchInput
              value={entitySearch}
              onChange={(_event, value) => setEntitySearch(value)}
              onClear={() => setEntitySearch('')}
              placeholder="Search entities..."
              aria-label="Search entities input"
            />
          </div>
          <Divider />
          {shown.map(name => (
            <SelectOption hasCheckbox key={name} value={name} isSelected={filters.entity.includes(name)}>
              {name}
            </SelectOption>
          ))}
          {shown.length === 0 && <SelectOption isDisabled value="No results">No results found</SelectOption>}
        </Select>
      </ToolbarFilter>
    );
  };

  const buildKeyFilter = () => (
    <ToolbarFilter
      categoryName="Key"
      showToolbarItem={currentCategory === 'Key'}
      deleteLabel={onDeleteFilter}
      labels={filters.key}
    >
      <SearchInput
        aria-label="Key filter"
        placeholder="Filter by key..."
        value={keyInput}
        onChange={(_event, value) => setKeyInput(value)}
        onClear={() => setKeyInput('')}
        onSearch={(_event, value) => onKeySearch(value)}
      />
    </ToolbarFilter>
  );

  const buildDateFilter = (category: DateCategory) => {
    const applied = appliedRanges[category];
    const input = rangeInputs[category];
    const asShown = (iso: string) => (iso ? ddMMyyyyFormat(fromIsoDate(iso)) : '');

    return (
      <ToolbarFilter
        key={category}
        categoryName={category}
        showToolbarItem={currentCategory === category}
        deleteLabel={onDeleteFilter}
        labels={applied && (applied.start || applied.end)
          ? [`${applied.start ? asShown(applied.start) : 'Any'} to ${applied.end ? asShown(applied.end) : 'Any'}`]
          : []}
      >
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <DatePicker
            value={asShown(input.start)}
            onChange={(_event, _value, date) => setRangeEnd(category, 'start', date)}
            aria-label={`${category} from`}
            dateFormat={dateFormater}
            dateParse={ddMMyyyyParse}
            placeholder="DD/MM/YYYY"
          />
          <span>to</span>
          <DatePicker
            value={asShown(input.end)}
            onChange={(_event, _value, date) => setRangeEnd(category, 'end', date)}
            aria-label={`${category} to`}
            dateFormat={dateFormater}
            dateParse={ddMMyyyyParse}
            placeholder="DD/MM/YYYY"
          />
          <Button variant="control" onClick={() => applyRange(category)} isDisabled={!input.start && !input.end}>
            Apply
          </Button>
        </div>
      </ToolbarFilter>
    );
  };

  const tableToolbar = (
    <Toolbar id="automation-messages-toolbar" clearAllFilters={clearAllFilters} collapseListedFiltersBreakpoint="xl">
      <ToolbarContent>
        <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
          <ToolbarGroup variant="filter-group" style={{ lineHeight: '22px', alignItems: 'center' }}>
            {buildCategoryDropdown()}
            {buildTypeFilter()}
            {buildSourceFilter()}
            {buildEntityFilter()}
            {buildKeyFilter()}
            {buildDateFilter('Created')}
            {buildDateFilter('Updated')}
          </ToolbarGroup>
        </ToolbarToggleGroup>
        <ToolbarGroup>
          <ToolbarItem>
            <Button
              variant="secondary"
              isDisabled={selectedIds.size === 0}
              onClick={() => handleMarkReadStatus(Array.from(selectedIds), true)}
            >
              Mark Read
            </Button>
          </ToolbarItem>
        <ToolbarItem>
          <Button
            variant="danger"
            onClick={() => setIsBulkDeleteModalOpen(true)}
            isDisabled={selectedIds.size === 0}
          >
            Delete Selected
          </Button>
        </ToolbarItem>
        </ToolbarGroup>
        <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  return (
    <React.Fragment>
      {/* Rendered into the layout's sticky group beside the breadcrumb, rather than stuck under it
          with an offset of its own: any figure hard-coded for the breadcrumb's height is wrong at
          another density or window width, and the leftover strip is where the table scrolls into
          view between the two bars. See DetailPageHeader. */}
      <DetailPageHeader
        title="Automation Messages"
        actions={<PageActionsMenu onRefresh={retry} />}
      />

      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <div style={{ padding: '24px' }}>
            {tableToolbar}
            {/* Frozen Actions column: the scroll container is the horizontal scroll it sticks to, and
                gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
            <InnerScrollContainer>
              <Table aria-label="Automation Messages Table" variant="compact" gridBreakPoint="">
                <Thead>
                  <Tr>
                    <Th
                      select={{
                        onSelect: (_event, isSelecting) => handleSelectAll(isSelecting),
                        isSelected: paginatedRows.length > 0 && selectedIds.size === paginatedRows.length
                      }}
                      aria-label="Select all rows"
                    />
                    <Th width={10} modifier="nowrap" sort={getSortParams(COLUMN.type)}>Type</Th>
                    {/* Sorted like the sections used to be ordered: one click gathers the sheet
                        import's findings above this service's inside every class at once. */}
                    <Th width={10} modifier="nowrap" sort={getSortParams(COLUMN.source)}>Source</Th>
                    {/* Entity and Key head their own section as well as their column, and the
                        column is what still sorts the sections - see sortedRows. */}
                    <Th width={10} modifier="nowrap" sort={getSortParams(COLUMN.entity)}>Entity</Th>
                    <Th width={10} modifier="nowrap" sort={getSortParams(COLUMN.key)}>Key</Th>
                    {/* nowrap on both date headers: the label and its sort arrow are wider than
                        the column, and left to wrap they take a second line the rows do not need. */}
                    <Th width={10} modifier="nowrap" sort={getSortParams(COLUMN.created)}>Created</Th>
                    <Th width={10} modifier="nowrap" sort={getSortParams(COLUMN.updated)}>Updated</Th>
                    {/* A floor rather than a share of the table: seven columns and a frozen
                        Actions column can squeeze a percentage width down to a couple of
                        characters a line, and prose at that width is unreadable. Below this the
                        table overflows instead, into the scroll the frozen column is there for. */}
                    <Th width={30} style={{ minWidth: '12rem' }}>Message</Th>
                    <Th width={10} isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                  </Tr>
                </Thead>

                {/* A section per class - the entity and the key that name it - so everything wrong
                    with one class is read in one place, whichever system found it. Flattened into
                    siblings rather than wrapped per section, tbody being where this table already
                    puts a row and its expansion. */}
                {pageSections.flatMap(section => [
                  <Tbody key={`class-${section.id}`}>
                    <Tr style={{ backgroundColor: groupHeaderBackground }}>
                      <Td colSpan={COLUMN_COUNT}>
                        <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
                          <FlexItem><strong>{section.entity}</strong></FlexItem>
                          <FlexItem><strong>{section.key}</strong></FlexItem>
                          <FlexItem>
                            {/* Spelt out on hover, a section running past the page being the case
                                where the number and the rows under it do not agree. */}
                            <Tooltip content={`${section.total} message(s) about this ${section.entity} match the current filters, across every page.`}>
                              <Badge isRead>{section.total}</Badge>
                            </Tooltip>
                          </FlexItem>
                          {/* Named only where there is more than one of them, and then because it is
                              the thing worth seeing on this panel: both systems have something to say
                              about this class, which until their names for it were resolved could not
                              be seen at all. With one source the column below already says which. */}
                          {section.sources.length > 1 && (
                            <FlexItem style={{ color: subtleText }}>
                              reported by {joinLabels(section.sources.map(sourceLabel))}
                            </FlexItem>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  </Tbody>,

                  // The index is the row's place in the whole page and not in the section: it is what
                  // the table counts a range selection in, and restarting it per section would make
                  // one shift-click select against the wrong rows.
                  ...section.rows.map((row, indexInSection) => {
                    const rowIndex = section.firstIndex + indexInSection;
                    const isExpanded = expandedIds.has(row.id);
                    const isSelected = selectedIds.has(row.id);

                    // Define the explicit font weight for the row's content
                    const rowFontWeight = row.read ? 'normal' : 'bold';

                    return (
                      <Tbody key={row.id} isExpanded={isExpanded}>
                        <Tr
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleToggleRow(row)}
                        >
                          <Td
                            select={{
                              rowIndex: rowIndex,
                              onSelect: (_event, isSelecting) => handleSelectRow(row.id, isSelecting),
                              isSelected,
                            }}
                            aria-label={`Select message ${row.id}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Td dataLabel="Type">
                            <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
                              <FlexItem><AutomationMessageTypeIcon type={row.type} /></FlexItem>
                              <FlexItem style={{ fontWeight: rowFontWeight }}>{row.type}</FlexItem>
                            </Flex>
                          </Td>
                          {/* What the section header cannot say, a class being reported by both:
                              which of the two systems this row came from, and on hover what that
                              means for whoever has to clear it. */}
                          <Td dataLabel="Source" modifier="nowrap">
                            <Tooltip content={sourceDetail(sourceOf(row))}>
                              <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
                                <FlexItem><AutomationMessageSourceIcon source={sourceOf(row)} /></FlexItem>
                                <FlexItem style={{ fontWeight: rowFontWeight }}>{sourceLabel(sourceOf(row))}</FlexItem>
                              </Flex>
                            </Tooltip>
                          </Td>
                          <Td dataLabel="Entity">
                            <span style={{ fontWeight: rowFontWeight }}>{row.entity}</span>
                          </Td>
                          <Td dataLabel="Key">
                            <span style={{ fontWeight: rowFontWeight }}>{row.key}</span>
                          </Td>
                          <Td dataLabel="Created" modifier="nowrap">
                            <span style={{ fontWeight: rowFontWeight }}>{renderDate(row.dateCreated)}</span>
                          </Td>
                          <Td dataLabel="Updated" modifier="nowrap">
                            <span style={{ fontWeight: rowFontWeight }}>{renderDate(row.dateUpdated)}</span>
                          </Td>
                          <Td dataLabel="Message">
                            <div style={{ whiteSpace: 'pre-line', fontWeight: rowFontWeight }}>
                              {row.message}
                            </div>
                          </Td>
                          <Td dataLabel="Actions" onClick={(e) => e.stopPropagation()}
                            isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>
                            <ActionList>
                              <ActionListItem>
                                <Tooltip content={row.read ? "Mark as unread" : "Mark as read"}>
                                  <Button
                                    icon={row.read ? <EnvelopeOpenIcon /> : <EnvelopeIcon />}
                                    variant="plain"
                                    onClick={() => handleMarkReadStatus([row.id], !row.read)}
                                  />
                                </Tooltip>
                              </ActionListItem>
                              <ActionListItem>
                                {/* Named by source as well as by class: the same class holds a row
                                    per system that reported it, so the class on its own no longer
                                    says which of the two rows this deletes. */}
                                <RemoveButton
                                  confirmationMessage={
                                    `Delete the ${sourceLabel(sourceOf(row))} message for ${row.entity} ${row.key}?`
                                  }
                                  onRemoveConfirm={() => handleDelete([row.id])}
                                />
                              </ActionListItem>
                            </ActionList>
                          </Td>
                        </Tr>

                        {/* Expanded Content Remains the Same */}
                        {isExpanded && (
                          <Tr isExpanded={isExpanded}>
                            <Td colSpan={COLUMN_COUNT}>
                              <ExpandableRowContent>
                                <div style={{ padding: '16px', backgroundColor: 'var(--pf-v6-global--BackgroundColor--200)', borderRadius: '4px' }}>
                                  <Title headingLevel="h6" size="md" style={{ marginBottom: '8px' }}>
                                    Entity Data (JSON)
                                  </Title>
                                  <div style={{
                                    backgroundColor: 'var(--pf-v6-global--BackgroundColor--100)',
                                    padding: '16px',
                                    border: '1px solid var(--pf-v6-global--BorderColor--100)',
                                    borderRadius: '4px',
                                    overflowX: 'auto'
                                  }}>
                                    {typeof parseJSON(row.value) === 'object' ? (
                                      <JsonViewer data={parseJSON(row.value)} initiallyExpanded={true} />
                                    ) : (
                                      <CodeBlock><CodeBlockCode>{parseJSON(row.value)}</CodeBlockCode></CodeBlock>
                                    )}
                                  </div>
                                </div>
                              </ExpandableRowContent>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    );
                  })
                ])}

                {/* Nothing to show: distinguish "the load failed" from "no records match" from
                    "the filters hide everything", which is the only one the reader can act on. */}
                {/* DataLoadErrorRow is a bare <Tr>, so it needs the Tbody the rows above supply
                    for themselves - without it React nests a row straight inside <table> and the
                    browser hoists it into a tbody of its own. */}
                {error && filteredRows.length === 0 && (
                  <Tbody>
                    <DataLoadErrorRow colSpan={COLUMN_COUNT} error={error} entityLabel="automation messages" onRetry={retry} />
                  </Tbody>
                )}
                {!error && filteredRows.length === 0 && (
                  <Tbody>
                    <Tr>
                      <Td colSpan={COLUMN_COUNT}>
                        {activeFilterCount > 0 ? (
                          <Bullseye>
                            <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No messages match the filters">
                              <EmptyStateBody>
                                No automation messages match the filter criteria. Clear all filters and try again.
                              </EmptyStateBody>
                              <EmptyStateFooter>
                                <EmptyStateActions>
                                  <Button variant="link" onClick={clearAllFilters}>Clear all filters</Button>
                                </EmptyStateActions>
                              </EmptyStateFooter>
                            </EmptyState>
                          </Bullseye>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '24px' }}>
                            No automation messages found.
                          </div>
                        )}
                      </Td>
                    </Tr>
                  </Tbody>
                )}
              </Table>
            </InnerScrollContainer>
            {renderPagination('bottom', false)}
          </div>
        </Card>
      </PageSection>

{/* Bulk Delete Confirmation Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        aria-labelledby="bulk-delete-modal-title"
        aria-describedby="bulk-delete-modal-body"
      >
        <ModalHeader
          title="Confirm Bulk Delete"
          titleIconVariant="danger"
          labelId="bulk-delete-modal-title"
        />

        <ModalBody id="bulk-delete-modal-body">
          <p>
            Are you sure you want to delete the <strong>{selectedIds.size}</strong> selected message(s)? This action cannot be undone.
          </p>
        </ModalBody>

        <ModalFooter>
          <Button
            key="confirm"
            variant="danger"
            onClick={() => {
              // Convert the Set to an array and pass it to your existing delete handler
              handleDelete(Array.from(selectedIds));
              setIsBulkDeleteModalOpen(false);
            }}
          >
            Delete {selectedIds.size} Message(s)
          </Button>
          <Button
            key="cancel"
            variant="link"
            onClick={() => setIsBulkDeleteModalOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  );
};

export { AutomationMessages };
